import { supabase } from './supabase';
import { ErrorDeApi } from './error-api';

export type PermisoPlataforma =
  | 'ver:indicadores'
  | 'ver:negocios'
  | 'escribir:negocios'
  | 'ver:analistas'
  | 'escribir:analistas'
  | 'ver:auditoria'
  | 'ver:catalogos'
  | 'escribir:catalogos'
  | 'ver:operaciones'
  | 'ver:metricas'
  | 'ver:sesiones'
  | 'escribir:sesiones';

export const PERMISOS: { valor: PermisoPlataforma; etiqueta: string }[] = [
  { valor: 'ver:indicadores', etiqueta: 'Ver indicadores de salud' },
  { valor: 'ver:negocios', etiqueta: 'Ver negocios' },
  { valor: 'escribir:negocios', etiqueta: 'Suspender y reactivar negocios' },
  { valor: 'ver:analistas', etiqueta: 'Ver el equipo de plataforma' },
  { valor: 'escribir:analistas', etiqueta: 'Administrar el equipo de plataforma' },
  { valor: 'ver:auditoria', etiqueta: 'Ver la auditoría' },
  { valor: 'ver:catalogos', etiqueta: 'Ver catálogos' },
  { valor: 'escribir:catalogos', etiqueta: 'Editar catálogos' },
  { valor: 'ver:operaciones', etiqueta: 'Consultar operaciones (solo metadatos)' },
  { valor: 'ver:metricas', etiqueta: 'Ver métricas agregadas' },
  { valor: 'ver:sesiones', etiqueta: 'Ver sesiones conectadas' },
  { valor: 'escribir:sesiones', etiqueta: 'Desconectar dispositivos' },
];

export interface PerfilPlataforma {
  rol: 'admin' | 'analista' | null;
  nombre: string | null;
  apellido: string | null;
  telefono: string | null;
  cargo: string | null;
  permisos: PermisoPlataforma[];
}

export async function miPerfilPlataforma(): Promise<PerfilPlataforma> {
  const { data, error, status } = await supabase.rpc('mi_perfil_plataforma');
  if (error) throw new ErrorDeApi(error, status);
  const d = (data ?? {}) as Partial<PerfilPlataforma>;
  return {
    rol: d.rol ?? null,
    nombre: d.nombre ?? null,
    apellido: d.apellido ?? null,
    telefono: d.telefono ?? null,
    cargo: d.cargo ?? null,
    permisos: d.permisos ?? [],
  };
}

/**
 * Los tres estados sin color piden acciones distintas: `sin_configurar` espera una credencial,
 * `sin_medir` es una prueba que no ha corrido, `sin_datos` no se sabe. Ninguno pinta rojo.
 */
export type NivelIndicador =
  | 'ok'
  | 'alerta'
  | 'critico'
  | 'sin_configurar'
  | 'sin_medir'
  | 'sin_datos';

export interface Indicador {
  clave: string;
  estado: NivelIndicador;
  umbral: string;
  detalle: Record<string, unknown>;
  /** El nombre visible, cuando lo pone la base: las tarjetas de servicio y de cuotas. */
  titulo?: string;
  /** Qué se rompe si esto falla, para el diálogo. */
  impacto?: string;
  /** El proveedor al que pertenece el indicador; agrupa el bloque en el Panel. */
  servicio?: string;
  /** Qué es ese proveedor para el negocio. Solo lo trae la tarjeta que abre el bloque. */
  paraQue?: string;
  /** El orden del bloque, declarado en la tabla `servicios`. */
  orden?: number;
  /**
   * El número de la cara con su unidad: la manda quien calcula el número, que es quien sabe si son
   * minutos, megabytes o cuentas.
   */
  cara?: { valor: number; unidad: string };
  /** El desglose de la cara, línea por línea, cuando la tarjeta suma varios casos. */
  lineas?: { texto: string; valor: number }[];
}

/** Campos que describen cómo se presenta el indicador, no lo que mide. */
const PRESENTACION = ['estado', 'umbral', 'titulo', 'impacto', 'servicio', 'para_que', 'orden', 'cara', 'lineas'];

export async function indicadoresPlataforma(): Promise<Indicador[]> {
  const { data, error, status } = await supabase.rpc('indicadores_plataforma');
  if (error) throw new ErrorDeApi(error, status);
  return Object.entries((data ?? {}) as Record<string, Record<string, unknown>>).map(([clave, v]) => ({
    clave,
    estado: (v.estado as NivelIndicador) ?? 'sin_datos',
    umbral: (v.umbral as string) ?? '',
    titulo: v.titulo as string | undefined,
    impacto: v.impacto as string | undefined,
    servicio: v.servicio as string | undefined,
    paraQue: v.para_que as string | undefined,
    orden: v.orden as number | undefined,
    cara: v.cara as { valor: number; unidad: string } | undefined,
    lineas: v.lineas as { texto: string; valor: number }[] | undefined,
    detalle: Object.fromEntries(Object.entries(v).filter(([k]) => !PRESENTACION.includes(k))),
  }));
}

/**
 * Los avisos de operación por negocio: de qué negocio es cada pendiente, que es lo primero que se
 * pregunta al ver un número distinto de cero.
 */
export interface AvisoPorNegocio {
  negocioId: string;
  negocio: string;
  tipo: string;
  pendientes: number;
  antiguedadDias: number | null;
  avisos: number;
  titulo: string;
  unidad: string;
}

/** Sale de la misma lectura que la pantalla del negocio, así que los dos números no pueden discrepar. */
export async function avisosPorNegocio(): Promise<AvisoPorNegocio[]> {
  const { data, error, status } = await supabase.rpc('avisos_por_negocio');
  if (error) throw new ErrorDeApi(error, status);
  type Fila = {
    negocio_id: string;
    negocio: string;
    tipo: string;
    pendientes: number;
    antiguedad_dias: number | null;
    avisos: number;
    titulo: string;
    unidad: string;
  };
  return ((data ?? []) as Fila[]).map((r) => ({
    negocioId: r.negocio_id,
    negocio: r.negocio,
    tipo: r.tipo,
    pendientes: r.pendientes,
    antiguedadDias: r.antiguedad_dias,
    avisos: r.avisos,
    titulo: r.titulo,
    unidad: r.unidad,
  }));
}

/**
 * Una línea del detalle de un aviso: qué reportó y cuántas veces. Un día abierto se reporta cada
 * mañana, así que tres avisos pueden ser dos días.
 */
export interface DetalleAviso {
  dato: string;
  veces: number;
  ultimo: string;
}

export async function detalleAviso(negocioId: string, tipo: string): Promise<DetalleAviso[]> {
  const { data, error, status } = await supabase.rpc('detalle_aviso', {
    p_negocio: negocioId,
    p_tipo: tipo,
  });
  if (error) throw new ErrorDeApi(error, status);
  return ((data ?? []) as { dato: string; veces: number; ultimo: string }[]).map((r) => ({
    dato: r.dato,
    veces: r.veces,
    ultimo: r.ultimo,
  }));
}

/** Cada cifra separa la operación real de los entornos de prueba, que no son actividad. */
export interface Conteo {
  real: number;
  prueba: number;
}

export interface MetricasPlataforma {
  ventasHoy: Conteo;
  ventasMes: Conteo;
  ventasTotal: Conteo;
  negociosConVentaHoy: Conteo;
  gastosMes: Conteo;
  /** Solo negocios reales: una curva de datos sembrados no significa nada. */
  tendencia7d: { fecha: string; ventas: number }[];
}

function conteo(v: unknown): Conteo {
  const c = (v ?? {}) as { real?: number; prueba?: number };
  return { real: Number(c.real ?? 0), prueba: Number(c.prueba ?? 0) };
}

export async function metricasPlataforma(): Promise<MetricasPlataforma> {
  const { data, error, status } = await supabase.rpc('metricas_plataforma');
  if (error) throw new ErrorDeApi(error, status);
  const d = (data ?? {}) as Record<string, unknown>;
  return {
    ventasHoy: conteo(d.ventas_hoy),
    ventasMes: conteo(d.ventas_mes),
    ventasTotal: conteo(d.ventas_total),
    negociosConVentaHoy: conteo(d.negocios_con_venta_hoy),
    gastosMes: conteo(d.gastos_mes),
    tendencia7d: ((d.tendencia_7d ?? []) as { fecha: string; ventas: number }[]).map((t) => ({
      fecha: String(t.fecha).slice(0, 10),
      ventas: Number(t.ventas),
    })),
  };
}

export interface NegocioPlataforma {
  id: string;
  nombre: string;
  estado: string;
  esSintetico: boolean;
  tipoPersona: string;
  docTipo: string | null;
  docNumero: string | null;
  razonSocial: string | null;
  telefono: string | null;
  /** Los correos de los propietarios del negocio, del más antiguo al más nuevo. */
  correos: string[];
  /** Su nombre visible, en el mismo orden que `correos`. */
  propietarios: string[];
  /** Cuándo autorizó el tratamiento de datos el propietario más antiguo. */
  consentimientoEn: string | null;
  /** Representante legal. `docs/adr/0018` deja el alta en persona natural, así que nadie lo escribe. */
  repDocTipo: string | null;
  repDocNumero: string | null;
  repCargo: string | null;
  miembros: number;
  ultimaVenta: string | null;
  creadoEn: string;
}

/** Con `documento`, solo los negocios cuyo documento lo contiene: el servidor busca y devuelve el número enmascarado. */
export async function listarNegociosPlataforma(documento?: string): Promise<NegocioPlataforma[]> {
  const { data, error, status } = await supabase.rpc('listar_negocios_plataforma', documento ? { p_documento: documento } : {});
  if (error) throw new ErrorDeApi(error, status);
  return ((data ?? []) as Record<string, unknown>[]).map((n) => ({
    id: n.id as string,
    nombre: n.nombre as string,
    estado: n.estado as string,
    esSintetico: n.es_sintetico as boolean,
    tipoPersona: n.tipo_persona as string,
    docTipo: (n.doc_tipo as string) ?? null,
    docNumero: (n.doc_numero as string) ?? null,
    razonSocial: (n.razon_social as string) ?? null,
    telefono: (n.telefono as string) ?? null,
    correos: (n.correos as string[]) ?? [],
    propietarios: (n.propietarios as string[]) ?? [],
    consentimientoEn: (n.consentimiento_en as string) ?? null,
    repDocTipo: (n.rep_doc_tipo as string) ?? null,
    repDocNumero: (n.rep_doc_numero as string) ?? null,
    repCargo: (n.rep_cargo as string) ?? null,
    miembros: Number(n.miembros ?? 0),
    ultimaVenta: (n.ultima_venta as string) ?? null,
    creadoEn: n.creado_en as string,
  }));
}

/** Crea la membresía de plataforma en un entorno de prueba y devuelve el negocio. */
export async function entrarSandbox(negocioId: string): Promise<string> {
  const { data, error, status } = await supabase.rpc('entrar_sandbox', { p_negocio: negocioId });
  if (error) throw new ErrorDeApi(error, status);
  return data as string;
}

export async function cambiarEstadoNegocio(
  negocioId: string,
  estado: 'activo' | 'suspendido',
  motivo: string
): Promise<void> {
  const { error, status } = await supabase.rpc('cambiar_estado_negocio', {
    p_negocio: negocioId,
    p_estado: estado,
    p_motivo: motivo || null,
  });
  if (error) throw new ErrorDeApi(error, status);
}

export interface Analista {
  usuarioId: string;
  correo: string;
  nombre: string | null;
  rol: 'admin' | 'analista';
  activo: boolean;
  permisos: PermisoPlataforma[];
  creadoEn: string;
}

export async function listarAnalistas(): Promise<Analista[]> {
  const { data, error, status } = await supabase.rpc('listar_analistas');
  if (error) throw new ErrorDeApi(error, status);
  return ((data ?? []) as Record<string, unknown>[]).map((a) => ({
    usuarioId: a.usuario_id as string,
    correo: a.correo as string,
    nombre: (a.nombre as string) ?? null,
    rol: a.rol as 'admin' | 'analista',
    activo: a.activo as boolean,
    permisos: (a.permisos ?? []) as PermisoPlataforma[],
    creadoEn: a.creado_en as string,
  }));
}

interface AccesoNuevo {
  correo: string;
  rol: 'analista' | 'admin';
  nombres: string;
  apellidos: string;
  celular: string;
  cargo: string;
  permisos: PermisoPlataforma[];
}

/** Da acceso al equipo por la Edge Function `dar-acceso`, que crea la cuenta si no existe y envía el código. */
export async function darAcceso(acceso: AccesoNuevo): Promise<{ codigoEnviado: boolean }> {
  const { data, error } = await supabase.functions.invoke<{ ok: boolean; codigoEnviado: boolean }>('dar-acceso', {
    body: { ...acceso, correo: acceso.correo.trim() },
  });
  if (error) {
    // Sin red, `context` es el error del fetch y no una respuesta: se trata como status 0.
    const contexto = (error as { context?: unknown }).context;
    const respuesta = contexto instanceof Response ? contexto : null;
    const cuerpo = respuesta ? ((await respuesta.json().catch(() => null)) as { error?: string } | null) : null;
    throw new ErrorDeApi({ message: cuerpo?.error ?? error.message }, respuesta?.status ?? 0);
  }
  return { codigoEnviado: data?.codigoEnviado === true };
}

export async function fijarPermisosAnalista(usuarioId: string, permisos: PermisoPlataforma[]): Promise<void> {
  const { error, status } = await supabase.rpc('fijar_permisos_analista', { p_usuario: usuarioId, p_permisos: permisos });
  if (error) throw new ErrorDeApi(error, status);
}

export async function activarAnalista(usuarioId: string, activo: boolean): Promise<void> {
  const { error, status } = await supabase.rpc('activar_analista', { p_usuario: usuarioId, p_activo: activo });
  if (error) throw new ErrorDeApi(error, status);
}

export interface EventoAuditoria {
  id: string;
  negocioId: string | null;
  negocio: string | null;
  tipo: string;
  payload: Record<string, unknown>;
  creadoEn: string;
  creadoVia: string;
  actor: string | null;
  total: number;
}

export async function auditoriaPlataforma(filtros: {
  tipos?: string[] | null;
  negocioId?: string | null;
  desde?: string | null;
  hasta?: string | null;
  limite?: number;
  desplazamiento?: number;
}): Promise<EventoAuditoria[]> {
  const { data, error, status } = await supabase.rpc('auditoria_plataforma', {
    p_tipos: filtros.tipos?.length ? filtros.tipos : null,
    p_negocio: filtros.negocioId ?? null,
    p_desde: filtros.desde ?? null,
    p_hasta: filtros.hasta ?? null,
    p_limite: filtros.limite ?? 50,
    p_desplazamiento: filtros.desplazamiento ?? 0,
  });
  if (error) throw new ErrorDeApi(error, status);
  return ((data ?? []) as Record<string, unknown>[]).map((e) => ({
    id: e.id as string,
    negocioId: (e.negocio_id as string) ?? null,
    negocio: (e.negocio as string) ?? null,
    tipo: e.tipo as string,
    payload: (e.payload ?? {}) as Record<string, unknown>,
    creadoEn: e.creado_en as string,
    creadoVia: e.creado_via as string,
    actor: (e.actor as string) ?? null,
    total: Number(e.total ?? 0),
  }));
}

export async function tiposEventoPlataforma(): Promise<{ tipo: string; veces: number }[]> {
  const { data, error, status } = await supabase.rpc('tipos_evento_plataforma');
  if (error) throw new ErrorDeApi(error, status);
  return ((data ?? []) as Record<string, unknown>[]).map((t) => ({
    tipo: t.tipo as string,
    veces: Number(t.veces ?? 0),
  }));
}

export const CATALOGOS_ADMINISTRABLES = [
  { tabla: 'dian_medios_pago', etiqueta: 'Medios de pago DIAN', clave: 'Código', valor: 'Nombre' },
  { tabla: 'dian_documentos_identidad', etiqueta: 'Tipos de documento DIAN', clave: 'Código', valor: 'Nombre' },
  { tabla: 'dian_responsabilidades', etiqueta: 'Responsabilidades fiscales', clave: 'Código', valor: 'Nombre' },
  { tabla: 'dian_uvt_anual', etiqueta: 'UVT por año', clave: 'Año', valor: 'Valor en pesos' },
] as const;

export async function listarCatalogoPlataforma(tabla: string): Promise<{ clave: string; nombre: string }[]> {
  const { data, error, status } = await supabase.rpc('listar_catalogo_plataforma', { p_tabla: tabla });
  if (error) throw new ErrorDeApi(error, status);
  return (data ?? []) as { clave: string; nombre: string }[];
}

export async function guardarCatalogoPlataforma(tabla: string, clave: string, nombre: string): Promise<void> {
  const { error, status } = await supabase.rpc('guardar_catalogo_plataforma', {
    p_tabla: tabla,
    p_clave: clave,
    p_nombre: nombre,
  });
  if (error) throw new ErrorDeApi(error, status);
}

export interface OperacionNegocio {
  consecutivo: string;
  fecha: string;
  estado: string;
  creadoVia: string;
  lineas: number;
  anuladaEn: string | null;
}

/** Metadatos, nunca montos ni datos del cliente: alcanza para responder "¿se registró la venta X?". */
export async function operacionesNegocio(
  negocioId: string,
  desde: string | null,
  hasta: string | null
): Promise<OperacionNegocio[]> {
  const { data, error, status } = await supabase.rpc('operaciones_negocio', {
    p_negocio: negocioId,
    p_desde: desde || null,
    p_hasta: hasta || null,
  });
  if (error) throw new ErrorDeApi(error, status);
  return ((data ?? []) as Record<string, unknown>[]).map((o) => ({
    consecutivo: o.consecutivo as string,
    fecha: o.fecha as string,
    estado: o.estado as string,
    creadoVia: o.creado_via as string,
    lineas: Number(o.lineas ?? 0),
    anuladaEn: (o.anulada_en as string) ?? null,
  }));
}
